import { useMemo, useState } from 'react'
import { useBrandMediaFiles } from 'hooks/queries/workspaces/brandVisualKits/useBrandMediaFiles'
import { BrandMediaType, LogoVariant } from 'types'
import { Hotspot } from 'hooks/queries/designs/useHotspots'
import { HotspotEdit, PopoverView } from './Types'
import Styles from './ImageEditor.module.scss'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useModals } from 'almanac-ui/Modal'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { MainView } from './MainView'
import { ConfirmDeletionView } from './ConfirmDeletionView'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { HotspotTrigger } from './HotspotTrigger'
import cx from 'classnames'
import { useAutopilotBrandKit } from 'hooks/queries/workspaces/useAutopilotBrandKit'
import type { BrandKitLogoOption } from './LogoHotspotPopoverView'

export type { PopoverView }

interface HotspotPopoverProps {
  hotspot: Hotspot
  globalIndex: number
  edits?: HotspotEdit[]
  operations: HotspotEdit['type'][]
  referenceImageUrl?: string | null
  onEditChange: (globalIndex: number, hotspot: Hotspot, edits: HotspotEdit[]) => void
}

const PADDING = 2 // px

const isTextSubject = (subject: Hotspot['subject_type']) => subject === 'text_box' || subject === 'call_to_action'

const urlForBrandMediaFile = (logo: { url?: string; media_file?: { source_url?: string } }) =>
  logo.url || logo.media_file?.source_url || null

/** When reopening a hotspot that already has these edits, skip the main menu where applicable. */
const initialViewForActiveEdits = (activeEdits: HotspotEdit[]): PopoverView => {
  if (activeEdits.some((e) => e.type === 'remove')) return 'confirmDeletion'
  return 'main'
}

export const HotspotPopover = ({
  hotspot,
  globalIndex,
  edits = [],
  operations,
  referenceImageUrl = null,
  onEditChange,
}: HotspotPopoverProps) => {
  const { openModal, closeModal } = useModals()
  const [currentView, setCurrentView] = useState<PopoverView>('main')
  const [prompt, setPrompt] = useState('')
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [replaceLogoUrl, setReplaceLogoUrl] = useState('')

  const { brandKit } = useAutopilotBrandKit()
  const brandVisualKitId = brandKit?.brand_visual_kit?.id
  const additionalLogosQueryResult = useBrandMediaFiles(
    {
      brandVisualKitId: brandVisualKitId ?? 0,
      brandMediaType: [BrandMediaType.LOGO],
      isPrimaryLogo: false,
    },
    { enabled: brandVisualKitId != null, cacheTime: 1000 * 60 * 5 },
  )
  const additionalLogosPayload = additionalLogosQueryResult?.data

  const brandKitLogos = useMemo((): BrandKitLogoOption[] => {
    const kit = brandKit?.brand_visual_kit
    const out: BrandKitLogoOption[] = []
    const seenUrls = new Set<string>()

    if (kit) {
      const primaryLogos = kit.logos?.filter((l) => l.is_primary_logo) ?? []
      const originalFromVariants = primaryLogos
        .filter((l) => l.logo_variant === LogoVariant.Original)
        .sort((a, b) => b.id - a.id)[0]
      let originalUrl = originalFromVariants ? urlForBrandMediaFile(originalFromVariants) : null
      let originalId = originalFromVariants?.id ?? 0
      let originalName = originalFromVariants?.name
      if (!originalUrl && kit.primary_logo) {
        originalUrl = urlForBrandMediaFile(kit.primary_logo)
        originalId = kit.primary_logo.id
        originalName = kit.primary_logo.name
      }
      if (originalUrl) {
        out.push({ id: originalId, url: originalUrl, name: originalName })
        seenUrls.add(originalUrl)
      }
    }

    const files = additionalLogosPayload?.brand_media_files ?? []
    for (const f of [...files].sort((a, b) => b.id - a.id)) {
      const url = urlForBrandMediaFile(f)
      if (!url || seenUrls.has(url)) continue
      seenUrls.add(url)
      out.push({ id: f.id, url, name: f.name })
    }

    return out
  }, [brandKit, additionalLogosPayload])

  const isEdited = edits.length > 0
  const isRemoved = edits?.some((e) => e.type === 'remove') ?? false
  const hasImageAttach = operations.includes('update_image')
  const isImageType = hotspot.subject_type === 'image'
  const isLogoType = hotspot.subject_type === 'logo'

  const resetLocalState = () => {
    const updateEdit = edits?.find((e) => e.type === 'update') as Extract<HotspotEdit, { type: 'update' }> | undefined
    const updateImageEdit = edits?.find((e) => e.type === 'update_image') as
      | Extract<HotspotEdit, { type: 'update_image' }>
      | undefined
    const replaceLogoEdit = edits?.find((e) => e.type === 'replace_logo') as
      | Extract<HotspotEdit, { type: 'replace_logo' }>
      | undefined

    setPrompt(updateEdit?.prompt ?? updateImageEdit?.prompt ?? '')
    setImageUrls(updateImageEdit?.imageUrls ?? [])
    setReplaceLogoUrl(replaceLogoEdit?.imageUrl ?? '')
    const nextView = edits.length > 0 ? initialViewForActiveEdits(edits) : 'main'
    setCurrentView(nextView)
  }

  const upsertEdit = (current: HotspotEdit[], edit: HotspotEdit): HotspotEdit[] => {
    const filtered = current.filter((e) => e.type !== edit.type)
    return [...filtered, edit]
  }

  const handleRemove = (close: () => void) => {
    onEditChange(globalIndex, hotspot, [{ type: 'remove', hotspot }])
    close()
  }

  const handleCancelDeletion = (close: () => void) => {
    const next = edits.filter((e) => e.type !== 'remove')
    onEditChange(globalIndex, hotspot, next)
    close()
  }

  const handleConfirmPrompt = (close: () => void) => {
    let current = edits

    if (hasImageAttach) {
      if (prompt.trim() || imageUrls.length > 0) {
        current = upsertEdit(current, {
          type: 'update_image',
          hotspot,
          prompt: prompt.trim() || undefined,
          imageUrls: imageUrls.length > 0 ? imageUrls : undefined,
        })
      } else {
        current = current.filter((e) => e.type !== 'update_image')
      }
    } else if (prompt.trim()) {
      current = upsertEdit(current, { type: 'update', hotspot, prompt: prompt.trim() })
    } else {
      current = current.filter((e) => e.type !== 'update')
    }

    onEditChange(globalIndex, hotspot, current)
    close()
  }

  const commitReplaceLogoEdits = () => {
    let current = edits

    if (replaceLogoUrl) {
      current = upsertEdit(current, { type: 'replace_logo', hotspot, imageUrl: replaceLogoUrl })
    } else {
      current = current.filter((e) => e.type !== 'replace_logo')
    }

    onEditChange(globalIndex, hotspot, current)
  }

  const handleConfirmLogoPopover = (close: () => void) => {
    commitReplaceLogoEdits()
    close()
  }

  const handlePickImage = () => {
    openModal(
      ImageMenuModal,
      {
        maxCount: 10,
        onUse: (images: ImageMenuSelectedImage[]) => {
          const newUrls = images.map((img) => img.url)
          setImageUrls((prev) => [...prev, ...newUrls])
        },
        onPressOutside: () => closeModal(),
      },
      { newStack: true },
    )
  }

  const handlePickReferenceImage = () => {
    openModal(
      ImageMenuModal,
      {
        maxCount: 1,
        onUse: (images: ImageMenuSelectedImage[]) => {
          setImageUrls(images.length > 0 ? [images[0].url] : [])
        },
        onPressOutside: () => closeModal(),
      },
      { newStack: true },
    )
  }

  const handlePickReplaceLogo = () => {
    openModal(
      ImageMenuModal,
      {
        maxCount: 1,
        onUse: (images: ImageMenuSelectedImage[]) => {
          if (images.length > 0) {
            setReplaceLogoUrl(images[0].url)
          }
        },
        onPressOutside: () => closeModal(),
      },
      { newStack: true },
    )
  }

  return (
    <ConditionalWrapper
      condition={!isImageType}
      wrapper={(children) => (
        <div
          className={Styles.boundingBoxWrapper}
          style={{
            left: `calc(${hotspot.x * 100}% - ${PADDING}px)`,
            top: `calc(${hotspot.y * 100}% - ${PADDING}px)`,
            width: `calc(${hotspot.width * 100}% + ${PADDING * 2}px)`,
            height: `calc(${hotspot.height * 100}% + ${PADDING * 2}px)`,
          }}
        >
          {children}
        </div>
      )}
    >
      <DropdownTrigger
        popoverClassName={cx(
          Styles.popover,
          ((isTextSubject(hotspot.subject_type) && currentView === 'main') || currentView === 'confirmDeletion') &&
            Styles.popoverEditText,
          currentView === 'main' && hotspot.subject_type === 'image' && Styles.popoverImageHotspot,
          currentView === 'main' && isLogoType && Styles.popoverLogoHotspot,
        )}
        root={document.getElementById('yolo')}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            resetLocalState()
          }
        }}
      >
        <HotspotTrigger hotspot={hotspot} isEdited={isEdited} isRemoved={isRemoved} index={globalIndex} />
        {(close) => (
          <>
            {currentView === 'main' && (
              <MainView
                hotspot={hotspot}
                operations={operations}
                hasTextDescribeEdit={edits.some((e) => e.type === 'update')}
                hasImageAttach={hasImageAttach}
                prompt={prompt}
                imageUrls={imageUrls}
                referenceImageUrl={referenceImageUrl}
                onPromptChange={setPrompt}
                onRemoveAttachedImage={(i) => setImageUrls((prev) => prev.filter((_, idx) => idx !== i))}
                onPickImage={handlePickImage}
                onPickReferenceImage={handlePickReferenceImage}
                onRemove={() => handleRemove(close)}
                onConfirmPrompt={() => handleConfirmPrompt(close)}
                close={close}
                brandKitLogos={brandKitLogos}
                replaceLogoUrl={replaceLogoUrl}
                onSelectBrandKitLogoUrl={(url) => {
                  if (url) setReplaceLogoUrl(url)
                }}
                onPickLogoUpload={handlePickReplaceLogo}
                onConfirmLogo={() => handleConfirmLogoPopover(close)}
                onRemoveLogo={() => handleRemove(close)}
              />
            )}
            {currentView === 'confirmDeletion' && (
              <ConfirmDeletionView
                onCancelDeletion={() => handleCancelDeletion(close)}
                onDismiss={() => close()}
                close={close}
              />
            )}
          </>
        )}
      </DropdownTrigger>
    </ConditionalWrapper>
  )
}
